import type { SearchFilters, PaginationConfig } from '@content-search/domain';
import { deserializeFiltersFromUrl, serializeFiltersToUrl, hasSearchParams } from '@content-search/utils';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook that manages URL state for content search filters and pagination.
 * This hook is responsible for serializing/deserializing filters to/from URL parameters.
 */
export function useSearchState() {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse filters and pagination from URL
  const [urlState, setUrlState] = useState(() => {
    const searchParams = new URLSearchParams(location.search);

    return deserializeFiltersFromUrl(searchParams);
  });

  // Track if we've performed a search (for UI state)
  const [hasSearched, setHasSearched] = useState(false);

  // Track if we've checked for initial search params (to avoid multiple auto-searches)
  const [hasCheckedInitialParams, setHasCheckedInitialParams] = useState(false);

  // Update URL state when location changes (browser back/forward)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const newUrlState = deserializeFiltersFromUrl(searchParams);

    setUrlState(newUrlState);
  }, [location.search]);

  // Unified function to update search state (filters and/or pagination)
  const updateSearchState = useCallback(
    (updates: { filters?: Partial<SearchFilters>; pagination?: Partial<PaginationConfig> }) => {
      const newFilters = updates.filters ? { ...urlState.filters, ...updates.filters } : urlState.filters;
      const newPagination = updates.pagination ? { ...urlState.pagination, ...updates.pagination } : urlState.pagination;

      const params = serializeFiltersToUrl(newFilters, newPagination);
      const newSearch = params.toString();
      const currentSearch = location.search.substring(1); // Remove the '?' prefix

      // Navigate if the URL search string changes (including empty to non-empty or vice versa)
      if (newSearch !== currentSearch) {
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

        navigate(newUrl, { replace: true });
      }
    },
    [navigate, location.pathname, location.search, urlState.filters, urlState.pagination]
  );

  // Auto-search on initial load if URL has search parameters
  useEffect(() => {
    if (!hasCheckedInitialParams) {
      setHasCheckedInitialParams(true);
      const searchParams = new URLSearchParams(location.search);

      if (hasSearchParams(searchParams)) {
        // We have search parameters, so we should auto-search
        // This will be handled by the ContentSearchPage component
        setHasSearched(true);
      }
    }
  }, [hasCheckedInitialParams, location.search]);

  return {
    // Search state from URL
    searchFilters: urlState.filters,
    pagination: urlState.pagination,
    hasSearched,

    // Actions
    updateSearchState,
    setHasSearched,
  };
}
